/**
 * Tool Display Configuration System
 *
 * Provides type-safe configuration for how LLM tools are displayed in chat.
 * Each tool can define its display name, loading text, output formatters, etc.
 */ /**
 * Category display information
 */ export const CATEGORY_METADATA = {
    file: {
        id: 'file',
        displayName: 'File Operations',
        description: 'List, read, and manage files in the chat workspace'
    },
    govcon: {
        id: 'govcon',
        displayName: 'Government Contracting',
        description: 'RFP analysis, bid scoring, contract intelligence'
    },
    gsa: {
        id: 'gsa',
        displayName: 'GSA',
        description: 'GSA Schedule pricing and contractor lookup'
    },
    corporate: {
        id: 'corporate',
        displayName: 'Corporate Intelligence',
        description: 'Corporate family trees, M&A research'
    },
    classifier: {
        id: 'classifier',
        displayName: 'Classifiers',
        description: 'NAICS, PSC, and other classification tools'
    },
    e2b: {
        id: 'e2b',
        displayName: 'Code Execution',
        description: 'Python code execution in sandboxed environment'
    },
    spreadsheet: {
        id: 'spreadsheet',
        displayName: 'Spreadsheets',
        description: 'Create and edit Excel spreadsheets'
    },
    presentation: {
        id: 'presentation',
        displayName: 'Presentations',
        description: 'Create and edit PowerPoint presentations'
    },
    text: {
        id: 'text',
        displayName: 'Text Documents',
        description: 'Create and edit text/markdown documents'
    },
    rag: {
        id: 'rag',
        displayName: 'RAG / Search',
        description: 'Document search and retrieval'
    },
    'web-search': {
        id: 'web-search',
        displayName: 'Web Search',
        description: 'Search the web using Linkup and Exa AI'
    }
};
/**
 * Single source of truth for a tool run's outcome, shared by the flat ToolCard
 * and the Workroom's collapsible disclosure so their status dot / tone can never
 * disagree. Mirrors ToolCard's result precedence exactly: explicit error
 * predicate → empty predicate → schema validity → success.
 *
 * Crucially, a run that fails schema validation (`isValidOutput === false`)
 * is an *error* even when the config defines no `isErrorResult` — many configs
 * (e.g. rfp-shredder, bid-no-bid-scorer, far-compliance-check) rely solely on
 * `isValidOutput`, and this is the case that must not read as success.
 */ export function classifyToolResult(config, output, isRunning) {
    if (isRunning) return 'running';
    // Treat null like undefined ("no output yet") — the predicates below
    // dereference `output`, so a null payload would otherwise throw.
    if (output === undefined || output === null) return 'pending';
    if (config.isErrorResult?.(output)) return 'error';
    if (config.isEmptyResult?.(output)) return 'empty';
    if (!config.isValidOutput(output)) return 'error';
    return 'success';
}
/**
 * Turn a tool id into a sensible humanized display name when the catalog
 * hasn't supplied one. "govcon__analyze-contract-v2" → "Govcon Analyze
 * Contract V2"; "naics-classifier" → "NAICS Classifier". The result is
 * deliberately rough — real names live in the backend YAML.
 */ export function humanizeToolId(id) {
    // Acronyms we want to preserve in all caps.
    const acronyms = new Set([
        'NAICS',
        'PSC',
        'SAM',
        'FPDS',
        'GSA',
        'SIN',
        'CLIN',
        'SOW',
        'PMP',
        'RFP',
        'RAG',
        'FAR',
        'AI',
        'SWOT',
        'PCS'
    ]);
    // Split on -, _, ., :: while preserving content boundaries.
    return id.replace(/[_:.]+/g, '-').split('-').filter(Boolean).map((token)=>{
        const upper = token.toUpperCase();
        if (acronyms.has(upper)) return upper;
        return token.charAt(0).toUpperCase() + token.slice(1);
    }).join(' ');
}
/**
 * Helper to create a type-safe tool config that's also assignable to AnyToolDisplayConfig
 */ export function defineToolConfig(config) {
    // Cast to AnyToolDisplayConfig for registry storage
    // The type guards ensure runtime safety
    return config;
}
/**
 * Helper to check if an object has a specific property
 */ export function hasProperty(obj, key) {
    return typeof obj === 'object' && obj !== null && key in obj;
}
/**
 * Helper to safely get a nested property
 */ export function getNestedProperty(obj, path) {
    const keys = path.split('.');
    let current = obj;
    for (const key of keys){
        if (!hasProperty(current, key)) {
            return undefined;
        }
        current = current[key];
    }
    return current;
}
/**
 * Helper to format a count with proper pluralization
 */ export function formatCount(count, singular, plural) {
    const pluralForm = plural ?? `${singular}s`;
    return `${count} ${count === 1 ? singular : pluralForm}`;
}
/**
 * Helper to format duration in human-readable form
 */ export function formatDuration(ms) {
    if (ms < 1000) {
        return `${ms}ms`;
    }
    const seconds = ms / 1000;
    if (seconds < 60) {
        return `${seconds.toFixed(1)}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
}
/**
 * Default error formatter
 */ export function defaultErrorFormatter(error) {
    if (typeof error === 'string') {
        return error;
    }
    if (hasProperty(error, 'message') && typeof error.message === 'string') {
        return error.message;
    }
    if (hasProperty(error, 'error') && typeof error.error === 'string') {
        return error.error;
    }
    return 'An unexpected error occurred';
}
/**
 * Wrap a formatter function with try-catch for runtime safety.
 * If the formatter throws (e.g., due to unexpected output shape), returns a fallback message.
 */ export function safeFormatter(formatter, fallback) {
    return (output)=>{
        try {
            return formatter(output);
        } catch  {
            return fallback;
        }
    };
}
/**
 * Safely access a numeric property with a default value
 */ export function safeNumber(value, defaultValue = 0) {
    return typeof value === 'number' && !Number.isNaN(value) ? value : defaultValue;
}
/**
 * Safely access a string property with a default value
 */ export function safeString(value, defaultValue = '') {
    return typeof value === 'string' ? value : defaultValue;
}
/**
 * Safely access an array property with a default value
 */ export function safeArray(value, defaultValue = []) {
    return Array.isArray(value) ? value : defaultValue;
}
/**
 * Safely access a boolean property with a default value
 */ export function safeBoolean(value, defaultValue = false) {
    return typeof value === 'boolean' ? value : defaultValue;
}
